<template>
	<div id="asset-mix-body">
    <h3 class="module-title">
      Asset Mix
      <disclosure-superscript class="detail-page" anchor="assetMix" :statistics="['assetMix']" />
    </h3>
    <div v-if="hasData" class="row">
      <div
		v-if="showAssetMixChart"
	  	class="col-md-3 col-sm-4 col-xs-4"
	  >
        <img class="chart" :src="chart">
      </div>
      <div class="two-column-table">
        <div :class="assetMixTableColumnClass">
          <table class="chart-legend">
            <tbody>
              <tr>
                <td>
                  <span :class="['colored-square', assetMixArrayData[0].color]" />
                  <span class="content">
                    {{ assetMixArrayData[0].assetType }}
                    <div class="data">{{ assetMixArrayData[0].percent }}</div>
                  </span>
                </td>
                <td>
                  <span :class="['colored-square', assetMixArrayData[1].color]" />
                  <span class="content">
                    {{ assetMixArrayData[1].assetType }}
                    <div class="data">{{ assetMixArrayData[1].percent }}</div>
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <span :class="['colored-square', assetMixArrayData[2].color]" />
                  <span class="content">
                    {{ assetMixArrayData[2].assetType }}
                    <div class="data">{{ assetMixArrayData[2].percent }}</div>
                  </span>
                </td>
                <td>
                  <span :class="['colored-square', assetMixArrayData[3].color]" />
                  <span class="content">
                    {{ assetMixArrayData[3].assetType }}
                    <div class="data">{{ assetMixArrayData[3].percent }}</div>
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <span :class="['colored-square', assetMixArrayData[4].color]" />
                  <span class="content">
                    {{ assetMixArrayData[4].assetType }}
                    <disclosure-superscript class="detail-page" anchor="assetMixCE" :statistics="['assetMixCE']" />
                    <div class="data">{{ assetMixArrayData[4].percent }}</div>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="sub-info">
            As of {{ asOfDate }} (updated monthly)
          </div>
        </div>
      </div>
      <div class="one-column-table">
        <div class="legend-column" :class="assetMixTableColumnClass">
          <table class="chart-legend">
            <tbody v-if="assetMixArrayData">
              <tr v-for="(item, index) in assetMixArrayData" :key="index">
                <td>
                  <span :class="['colored-square', item.color]" />
                  <span class="content" :class="{ 'no-border' : index == 4 }">
                    {{ item.assetType }}
                    <slot v-if="item.assetType == 'Cash & Equivalents'" name="superscript3" />
                    <div class="data">{{ item.percent }}</div>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="sub-info">
            As of {{ asOfDate }} (updated monthly)
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-data">
      No asset mix data available at this time.
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
export default {
	name: 'AssetMixModule',
	computed: {
		...mapGetters(['getAssetMixData']),
		hasData() {
			// count the data points that have percentages that are not 0.0%, if all are 0.0% then there is no data
			const nonZero = this.assetMixArrayData.filter(obj => obj.percent !== '0.0%');
			return !!(this.assetMixArrayData && this.assetMixArrayData.length && nonZero.length);
		},
		showAssetMixChart() {
			return !this.assetMixArrayData.some(obj => parseInt(obj.percent) < 0);
		},
		assetMixTableColumnClass() {
			return this.showAssetMixChart ? "col-md-9 col-sm-8 col-xs-8" : "col-md-12 col-sm-12 col-xs-12";
		},
		componentData() {
			return this.getAssetMixData || {};
		},
		asOfDate() {
			return this.componentData.asOfDate || '--';
		},
		chart() {
			return this.componentData.assetMixChart;
		},
		assetMixArrayData() {
			// the api should use these hex colors for each asset type when building the pie chart.
			/*eslint-disable quote-props*/
			const colors = {
				'1e5aa0': 'dark-blue-fill',
				'33aee5': 'light-blue-fill',
				'008f45': 'green-fill',
				'81b24f': 'light-green-fill',
				'f2b900': 'yellow-fill'
			};
			let data = [];
			// afis uses assetMix and CIT uses rawAssetMix
			if (this.componentData.assetMix) data = this.componentData.assetMix;
			else if (this.componentData.rawAssetMixData) {
				// the format for the raw data should be an array of objects containing { Percent, Color, SubTypeName }
				data = this.componentData.rawAssetMixData.map(val => {
					return {
						percent: $format.rawPercent(val.Percent, 1),
						color: colors[val.Color],
						assetType: val.SubTypeName
					};
				});
			}
			return data;
		}
	},
	watch: {
		getAssetMixData: {
			immediate: true,
			handler(val) {
				this.$EventBus.$emit('ExcelData', 'AssetMixModule', val);
			}
		}
	}
};
</script>
<style lang="less" scoped>
@import '~_css_/variables';
#asset-mix-body{
	.module-border-top();
	margin-bottom: 35px;
	.two-column-table {
		display: block;
	}
	.one-column-table {
		display: none;
	}
	.chart {
		height: 98.5px;
		width: 98.5px;
	}
	.chart-legend {
		border-spacing: 0;
		width: 100%;
		margin-bottom: 0;
		td {
			padding: 0;
		}
		.content {
			.thin-light-gray-border-bottom();
			color: @gray;
			display: inline-block;
			font-family:"AvenirNextLTW01-Regular",serif;
			font-size: 12px;
			margin: 0;
			padding: 5px 0;
			width: 85%;
			&.no-border {
				border: none;
			}
		}
		.data {
			color: @dark-gray;
			float: right;
			font-family:"Avenir Next LT W01 Demi",Arial, serif;
		}
	}
	.sub-info {
		margin-left: 21px;
		margin-top: 15px;
		font-size: 12px;
		font-family: "AvenirNextLTW01-Condens 721299", Arial;
		color: @gray;
	}
	.colored-square {
		margin: 0 5px -1px 5px;
	}
	.dark-blue-fill {
		background-color: #1e5aa0;
	}
	.light-blue-fill {
		background-color: #33aee5;
	}
	.green-fill {
		background-color: #008f45;
	}
	.light-green-fill {
		background-color: #81b24f;
	}
	.yellow-fill {
		background-color: #f2b900;
	}
	@media (max-width: 991px) {
		.two-column-table {
			display: none;
		}
		.one-column-table {
			display: block;
		}
	}
	@media (max-width: 768px) {
		.chart-legend {
			.content {
				width: 90%;
			}
		}
	}
	@media (max-width: 480px) {
		.legend-column {
			width: 100%;
		}
	}
}
</style>
